import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiGet, apiPost, apiPut, apiDelete } from 'services/api';
import logger from 'utils/logger';
import type {
  ProviderCredential,
  ProviderCredentialCreateRequest,
  ProviderCredentialUpdateRequest,
} from '../types/provider-credentials.types';

const QUERY_KEY = 'admin-provider-credentials';

const ProviderCredentialSchema = z.object({
  id: z.string(),
  providerType: z.string(),
  providerName: z.string(),
  keyName: z.string(),
  credentials: z.object({ api_key: z.string() }),
  usageType: z.string(),
  maxConcurrentSessions: z.number().nullable(),
  priority: z.number(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string().nullable(),
});

function transformSnakeToCamel<T>(obj: Record<string, unknown>): T {
  const transformed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
    transformed[camelKey] = value;
  }
  return transformed as T;
}

const fetchCredentials = async (): Promise<ProviderCredential[]> => {
  try {
    const data = await apiGet<unknown[]>('/api/admin/provider-credentials');
    return z
      .array(ProviderCredentialSchema)
      .parse(
        data.map((item) => transformSnakeToCamel(item as Record<string, unknown>))
      ) as ProviderCredential[];
  } catch (error) {
    logger.error('Error fetching provider credentials:', error);
    throw error;
  }
};

const createCredential = async (
  request: ProviderCredentialCreateRequest
): Promise<ProviderCredential> => {
  try {
    const data = await apiPost<unknown>('/api/admin/provider-credentials', request);
    return ProviderCredentialSchema.parse(
      transformSnakeToCamel(data as Record<string, unknown>)
    ) as ProviderCredential;
  } catch (error) {
    logger.error('Error creating provider credential:', error);
    throw error;
  }
};

const updateCredential = async (
  id: string,
  request: ProviderCredentialUpdateRequest
): Promise<ProviderCredential> => {
  try {
    const data = await apiPut<unknown>(`/api/admin/provider-credentials/${id}`, request);
    return ProviderCredentialSchema.parse(
      transformSnakeToCamel(data as Record<string, unknown>)
    ) as ProviderCredential;
  } catch (error) {
    logger.error('Error updating provider credential:', error);
    throw error;
  }
};

const deleteCredential = async (id: string): Promise<void> => {
  try {
    await apiDelete(`/api/admin/provider-credentials/${id}`);
  } catch (error) {
    logger.error('Error deleting provider credential:', error);
    throw error;
  }
};

export const useProviderCredentials = () => {
  return useQuery({
    queryKey: [QUERY_KEY],
    queryFn: fetchCredentials,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateProviderCredential = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ProviderCredentialCreateRequest) => createCredential(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useUpdateProviderCredential = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: ProviderCredentialUpdateRequest }) =>
      updateCredential(id, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useDeleteProviderCredential = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCredential(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};
